import { ArrowLeft } from 'lucide-react';
import { TEAM_MODE_GRADIENT } from '../game/teamPaint';
import { trackEvent } from '../../helpers/utils/analytics';
import {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
} from 'react';
import { useModalViewport } from '../../helpers/utils/useModalViewport';
import { holdPageHeight } from '../../helpers/utils/holdPageHeight';
import '../modals/modals.css';
import { useScreenLayout } from '../../helpers/utils/useScreenLayout';
import { RAIL_WIDTH, RAIL_MARGIN } from '../../helpers/utils/screenFit';
import { Box, Flex } from '@chakra-ui/react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogCloseTrigger,
  DialogBlurLayer,
} from './dialog';
import type { ReactNode } from 'react';
import type { BoxProps, DialogRootProps } from '@chakra-ui/react';

/**
 * Figma Emerald/AppModal Tone: sets the border and the eyebrow colour. Accent
 * is Maple (accent/base), the same wood tone as the nil-rules help ball.
 */
export type AppModalTone = 'neutral' | 'team' | 'brand' | 'accent' | 'danger';

const TONE_BORDER: Record<AppModalTone, string> = {
  neutral: 'var(--spades-border-strong)',
  team: 'var(--spades-team-base)',
  brand: 'var(--spades-accent-frame)',
  accent: 'var(--spades-accent-base)',
  danger: 'var(--spades-error)',
};

const gradientText = (top: string, middle: string, bottom: string) => ({
  backgroundImage: `linear-gradient(180deg, ${top} 0%, ${middle} 35%, ${bottom} 100%)`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
});

const TONE_EYEBROW: Record<AppModalTone, object> = {
  neutral: { color: 'var(--spades-muted)' },
  team: {
    backgroundImage: TEAM_MODE_GRADIENT,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
  },
  brand: gradientText(
    'var(--spades-accent-eyebrow-top)',
    'var(--spades-accent-eyebrow-top)',
    'var(--spades-accent-eyebrow-bottom)',
  ),
  accent: { color: 'var(--spades-accent-base)' },
  danger: { color: 'var(--spades-error)' },
};

export interface AppModalProps extends Omit<
  DialogRootProps,
  'children' | 'closeOnInteractOutside' | 'closeOnEscape'
> {
  analyticsId?:
    | 'settings'
    | 'share'
    | 'viewer_seat'
    | 'dealer'
    | 'scoring'
    | 'game_over'
    | 'reset'
    | 'score_limit'
    | 'score_help'
    | 'game_score_help'
    | 'feedback_support'
    | 'error';
  isOpen: boolean;
  onClose: (isOpen: boolean) => void;
  title?: string;
  titleContent?: ReactNode;
  /** Small caps line above the title (Figma "eyebrow"). */
  eyebrow?: string;
  tone?: AppModalTone;
  children: ReactNode;
  contentStyle?: BoxProps;
  headerStyle?: BoxProps;
  bodyStyle?: BoxProps;
  closeButtonColor?: string;
  contentProps?: BoxProps;
  showCloseButton?: boolean;
  /** Allow the close button, Escape and outside interactions to dismiss. */
  dismissible?: boolean;
  onBack?: () => void;
  backLabel?: string;
}

function AppModal({
  isOpen,
  analyticsId = 'error',
  onClose,
  title,
  titleContent,
  eyebrow,
  tone = 'neutral',
  children,
  contentStyle = {},
  headerStyle = {},
  bodyStyle = {},
  closeButtonColor = 'spades.ivory',
  contentProps = {},
  showCloseButton = true,
  dismissible = true,
  onBack,
  backLabel = 'Back',
  ...rest
}: AppModalProps) {
  const viewport = useModalViewport();
  const layout = useScreenLayout();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panelMounted, setPanelMounted] = useState(false);
  const measurePanel = useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node;
    setPanelMounted(Boolean(node));
  }, []);
  const [naturalHeight, setNaturalHeight] = useState(0);
  const wideSettings =
    layout !== 'phone' && contentProps.className?.includes('settings-two-pane');
  const cap = wideSettings ? 1.1 : 1.2;
  const designWidth = wideSettings ? 732 : 358;
  const scale =
    layout === 'phone'
      ? 1
      : Math.min(
          cap,
          (viewport.height - 48) / (naturalHeight || 1),
          (window.innerWidth - 48) / designWidth,
        );
  const centre =
    layout === 'desktop'
      ? Math.max(
          24 + (designWidth * scale) / 2,
          (window.innerWidth - RAIL_WIDTH - RAIL_MARGIN) / 2,
        )
      : window.innerWidth / 2;
  const isNilHelp =
    analyticsId === 'score_help' ||
    (analyticsId === 'settings' && title === 'Score Settings');
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel || !isOpen || layout === 'phone') return;
    const measure = () => {
      const body = panel.querySelector<HTMLElement>('.emerald-modal-body');
      const header = panel.querySelector<HTMLElement>('.emerald-modal-header');
      const css = getComputedStyle(panel);
      // Measure the unconstrained content, excluding entrance transforms.
      const previousMaxHeight = panel.style.maxHeight;
      const previousBodyFlex = body?.style.flex;
      panel.style.maxHeight = 'none';
      if (body) body.style.flex = 'none';
      const height =
        (header?.offsetHeight ?? 0) +
        (body?.scrollHeight ?? 0) +
        parseFloat(css.paddingBottom || '0') +
        parseFloat(css.paddingTop || '0') +
        parseFloat(css.borderTopWidth || '0') +
        parseFloat(css.borderBottomWidth || '0');
      panel.style.maxHeight = previousMaxHeight;
      if (body) body.style.flex = previousBodyFlex ?? '';
      // F7: the multi-rule help sheet keeps its pinned header and scrolls;
      // fitting its entire scrollHeight would make the examples unreadable.
      setNaturalHeight(
        isNilHelp ? Math.min(height, (viewport.height - 48) / cap) : height,
      );
    };
    measure();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(measure);
    observer?.observe(panel);
    const header = panel.querySelector('.emerald-modal-header');
    if (header) observer?.observe(header);
    panel
      .querySelectorAll('.emerald-modal-body > *')
      .forEach((child) => observer?.observe(child));
    return () => observer?.disconnect();
  }, [panelMounted, isOpen, layout, children, isNilHelp, viewport.height, cap]);
  // The page under an open modal can't get shorter (holdPageHeight): on
  // Brave for iOS that moves the viewport, and with it the modal.
  useLayoutEffect(() => (isOpen ? holdPageHeight() : undefined), [isOpen]);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [canScrollMore, setCanScrollMore] = useState(false);
  useEffect(() => {
    const body = bodyRef.current;
    if (!body || !isOpen || !isNilHelp) return;
    const update = () =>
      setCanScrollMore(
        body.scrollHeight - body.clientHeight - body.scrollTop > 1,
      );
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(update);
    observer?.observe(body);
    for (const child of body.children) observer?.observe(child);
    body.addEventListener('scroll', update);
    update();
    return () => {
      observer?.disconnect();
      body.removeEventListener('scroll', update);
    };
  }, [isOpen, isNilHelp, children]);
  const dismissMethod = useRef<'close' | 'escape' | 'outside'>('outside');
  const outsideTouch = useRef(false);
  return (
    <DialogRoot
      open={isOpen}
      // Open on the panel, not its first button: Safari draws its focus box
      // around a button focused from code, even after a mouse click. Tab still
      // reaches the X first, and a dialog can pick its own target instead.
      initialFocusEl={() => panelRef.current}
      onEscapeKeyDown={() => {
        dismissMethod.current = 'escape';
      }}
      onPointerDownOutside={() => {
        dismissMethod.current = 'outside';
      }}
      onOpenChange={(e) => {
        if (!e.open) {
          trackEvent('modal_dismissed', {
            modal: analyticsId,
            method: dismissMethod.current,
          });
          dismissMethod.current = 'outside';
        }
        onClose(e.open);
      }}
      {...rest}
      preventScroll={false}
      closeOnInteractOutside={dismissible}
      closeOnEscape={dismissible}
    >
      {/* One static, full-viewport overlay carries both the dim and the blur so
          it mounts and unmounts in a single commit. The visuals go on an
          absolute child (see DialogBlurLayer): Safari 26 tints its chrome from
          background/backdrop-filter on fixed elements near the viewport
          edges, and that re-tint lags at the top/bottom on open and close. */}
      {isOpen && (
        <DialogBlurLayer
          data-testid="modal-blur-layer"
          layerStyle={{
            // Emerald/Scrim: color/background at 72% with a 2px blur.
            backgroundColor: 'rgba(2, 18, 30, 0.72)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
          }}
        />
      )}
      <DialogContent
        onPositionerPointerDown={(event) => {
          outsideTouch.current =
            event.target === event.currentTarget &&
            event.pointerType === 'touch';
        }}
        onPositionerClick={(event) => {
          if (!outsideTouch.current || event.target !== event.currentTarget)
            return;
          outsideTouch.current = false;
          // Touch outside-dismissal waits for click; React portal ancestors can
          // stop that click or act on it before Chakra's document listener.
          event.stopPropagation();
          if (dismissible) {
            trackEvent('modal_dismissed', {
              modal: analyticsId,
              method: 'outside',
            });
            onClose(false);
          }
        }}
        ref={measurePanel}
        aria-label={title}
        data-layout={layout}
        data-wide-settings={wideSettings || undefined}
        // Emerald/AppModal Dialog: 358pt (or the screen less 16pt a side), a
        // lifted panel with a 22pt radius and a tone-coloured 1.5pt border.
        className="emerald-modal"
        outline="none"
        maxH={`${Math.max(0, layout === 'phone' ? viewport.height - 32 : (viewport.height - 48) / scale)}px`}
        position="relative"
        top={`${(viewport.offsetTop + (viewport.height - window.innerHeight) / 2) / scale}px`}
        left={
          layout === 'phone'
            ? undefined
            : `${(centre - window.innerWidth / 2) / scale}px`
        }
        zoom={scale}
        display="flex"
        flexDirection="column"
        minH="0"
        overflow="hidden"
        w={wideSettings ? '732px' : 'min(358px, calc(100vw - 32px))'}
        maxW="none"
        bg="spades.surface"
        backgroundImage="var(--spades-panel-gradient)"
        boxShadow="0 16px 40px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
        borderColor={TONE_BORDER[tone]}
        borderWidth="1.5px"
        borderRadius="var(--spades-radius-lg)"
        pb="16px"
        mx={4}
        my="auto"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        {...contentStyle}
        {...contentProps}
      >
        {title && (
          <DialogHeader
            className="emerald-modal-header"
            data-testid="modal-header"
            flexShrink={0}
            minH="56px"
            display="flex"
            alignItems="center"
            pt="12px"
            px="14px"
            pb="0"
            {...headerStyle}
          >
            {onBack ? (
              <button
                type="button"
                className="modal-back"
                aria-label={backLabel}
                onClick={onBack}
              >
                <ArrowLeft size={24} aria-hidden="true" />
              </button>
            ) : (
              dismissible && showCloseButton && <Box w="44px" flexShrink={0} />
            )}
            <Flex
              direction="column"
              align="center"
              flex="1"
              minW="1px"
              gap="2px"
              pt="2px"
              textAlign="center"
            >
              {eyebrow && (
                <Box
                  fontSize="var(--spades-font-size-2xs)"
                  fontWeight="700"
                  lineHeight={1.25}
                  letterSpacing="var(--spades-tracking-xl)"
                  textTransform="uppercase"
                  {...TONE_EYEBROW[tone]}
                >
                  {eyebrow}
                </Box>
              )}
              <Box
                fontSize="var(--spades-font-size-lg)"
                fontWeight="800"
                lineHeight={1.1}
                color="spades.ivory"
              >
                {titleContent ?? title}
              </Box>
            </Flex>
            {((dismissible && showCloseButton) || onBack) && (
              <Box w="44px" flexShrink={0} />
            )}
          </DialogHeader>
        )}
        {dismissible && showCloseButton && (
          <DialogCloseTrigger
            onClickCapture={() => {
              dismissMethod.current = 'close';
            }}
            color={closeButtonColor}
            top="12px"
            right="14px"
          />
        )}
        <DialogBody
          data-testid="modal-body"
          ref={bodyRef}
          className="emerald-modal-body"
          minH="0"
          flex="0 1 auto"
          overflowY="auto"
          px="14px"
          pt="14px"
          pb="0"
          {...bodyStyle}
        >
          {children}
        </DialogBody>
        {isNilHelp && (
          <Box
            data-testid="modal-scroll-fade"
            hidden={!canScrollMore}
            pointerEvents="none"
            position="absolute"
            bottom="16px"
            left="2px"
            right="10px"
            h="72px"
            backgroundImage="linear-gradient(180deg, transparent, var(--spades-surface))"
          />
        )}
      </DialogContent>
    </DialogRoot>
  );
}

export default AppModal;
